import { api as coreApi } from "@/core_bridge/switch-core/index";
import { parseJwtPayload, setToken } from "@/core_bridge/module_api";
import { appConfig } from "@/app/app-config";
import { db as storage } from "@/services/module_api";
import { openDatabase, updateAppToken } from "@/services/module_api";
import { ValueSignal } from "./state_signal";

const SESSION_KEYS = {
  discriminator: "session:discriminator",
  recoveryPassphrase: "session:recoveryPassphrase",
  hiLoEnabled: "session:hiloEnabled",
  hiLoEnvironment: "session:hiloEnvironment",
} as const;

export type HiLoUpdate = {
  enabled: boolean;
  environment: string | null;
  unlocked: boolean;
};

export class SessionState {
  private token_value: string | null = null;
  private discriminator_value: string | null = null;
  private recovery_passphrase_value: string | null = null;
  private active_thread_value: string | null = null;
  private hi_lo_enabled = false;
  private hi_lo_environment: string | null = null;
  private hi_lo_unlocked = false;
  private initialized = false;
  private init_promise: Promise<void> | null = null;

  readonly tokenUpdates = new ValueSignal<string | null>(null);
  readonly discriminatorUpdates = new ValueSignal<string | null>(null);
  readonly activeThreadUpdates = new ValueSignal<string | null>(null);
  readonly hiLoUpdates = new ValueSignal<HiLoUpdate>({
    enabled: false,
    environment: null,
    unlocked: false,
  });

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.init_promise) return this.init_promise;

    this.init_promise = (async () => {
      await openDatabase();

      let stored_token = await storage.getSetting("app:token");
      if (!stored_token) {
        try {
          stored_token = localStorage.getItem("apiToken") || null;
        } catch {
          stored_token = null;
        }
      }

      if (stored_token) {
        await updateAppToken(stored_token);
      }

      const [stored_discriminator, stored_recovery, stored_hilo_enabled, stored_hilo_env] = await Promise.all([
        storage.getSetting(SESSION_KEYS.discriminator),
        storage.getSetting(SESSION_KEYS.recoveryPassphrase),
        storage.getSetting(SESSION_KEYS.hiLoEnabled),
        storage.getSetting(SESSION_KEYS.hiLoEnvironment),
      ]);

      this.token_value = stored_token || null;
      this.discriminator_value = stored_discriminator || null;
      this.recovery_passphrase_value = stored_recovery || null;
      this.hi_lo_enabled = stored_hilo_enabled === "true";
      this.hi_lo_environment = this.hi_lo_enabled ? (stored_hilo_env || null) : null;
      this.hi_lo_unlocked = false;

      this.tokenUpdates.set(this.token_value);
      this.discriminatorUpdates.set(this.discriminator_value);
      this.activeThreadUpdates.set(this.active_thread_value);
      this.hiLoUpdates.set({
        enabled: this.hi_lo_enabled,
        environment: this.hi_lo_environment,
        unlocked: this.hi_lo_unlocked,
      });

      if (this.token_value) {
        this.sync_core_token(this.token_value);
      }

      if (this.token_value && !this.discriminator_value) {
        const parsed = parseJwtPayload(this.token_value) || null;
        const discriminator = (parsed?.discriminator as string) || null;
        if (discriminator) {
          await this.setDiscriminator(discriminator);
        }
      }

      if (stored_token) {
        try {
          localStorage.removeItem("apiToken");
        } catch {
          // ignore
        }
      }

      this.initialized = true;
    })();

    return this.init_promise;
  }

  getToken(): string | null {
    return this.token_value;
  }

  async setToken(token: string | null): Promise<void> {
    if (token === this.token_value) return;
    await openDatabase();

    if (token) {
      await updateAppToken(token);
    } else {
      await storage.deleteSetting("app:token");
    }

    this.token_value = token;
    this.tokenUpdates.set(token);
    this.sync_core_token(token);

    if (token) {
      const parsed = parseJwtPayload(token) || null;
      const discriminator = (parsed?.discriminator as string) || null;
      if (discriminator) {
        await this.setDiscriminator(discriminator);
      }
    }
  }

  async clearToken(): Promise<void> {
    await this.setToken(null);
  }

  getDiscriminator(): string | null {
    return this.discriminator_value;
  }

  async setDiscriminator(value: string | null): Promise<void> {
    if (value === this.discriminator_value) return;
    await openDatabase();

    if (value) {
      await storage.setSetting(SESSION_KEYS.discriminator, value);
    } else {
      await storage.deleteSetting(SESSION_KEYS.discriminator);
    }

    this.discriminator_value = value;
    this.discriminatorUpdates.set(value);
  }

  getRecoveryPassphrase(): string | null {
    return this.recovery_passphrase_value;
  }

  async setRecoveryPassphrase(value: string | null): Promise<void> {
    if (value === this.recovery_passphrase_value) return;
    await openDatabase();

    if (value) {
      await storage.setSetting(SESSION_KEYS.recoveryPassphrase, value);
    } else {
      await storage.deleteSetting(SESSION_KEYS.recoveryPassphrase);
    }

    this.recovery_passphrase_value = value;
  }

  getActiveThreadId(): string | null {
    return this.active_thread_value;
  }

  setActiveThreadId(thread_id: string | null): void {
    if (thread_id === this.active_thread_value) return;
    this.active_thread_value = thread_id;
    this.activeThreadUpdates.set(thread_id);
  }

  isHiLoEnabled(): boolean {
    return this.hi_lo_enabled;
  }

  getHiLoEnvironment(): string | null {
    return this.hi_lo_environment;
  }

  async setHiLoEnabled(enabled: boolean): Promise<void> {
    if (enabled === this.hi_lo_enabled) return;
    await openDatabase();
    await storage.setSetting(SESSION_KEYS.hiLoEnabled, enabled ? "true" : "false");
    if (!enabled) {
      await storage.deleteSetting(SESSION_KEYS.hiLoEnvironment);
      this.hi_lo_environment = null;
    }
    this.hi_lo_enabled = enabled;
    this.hiLoUpdates.set({
      enabled: this.hi_lo_enabled,
      environment: this.hi_lo_environment,
      unlocked: this.hi_lo_unlocked,
    });
  }

  async setHiLoEnvironment(env: string | null): Promise<void> {
    if (env === this.hi_lo_environment) return;
    await openDatabase();

    if (env) {
      await storage.setSetting(SESSION_KEYS.hiLoEnvironment, env);
      await storage.setSetting(SESSION_KEYS.hiLoEnabled, "true");
      this.hi_lo_enabled = true;
    } else {
      await storage.deleteSetting(SESSION_KEYS.hiLoEnvironment);
    }

    this.hi_lo_environment = env;
    this.hiLoUpdates.set({
      enabled: this.hi_lo_enabled,
      environment: this.hi_lo_environment,
      unlocked: this.hi_lo_unlocked,
    });
  }

  isHiLoUnlocked(): boolean {
    return this.hi_lo_unlocked;
  }

  setHiLoUnlocked(value: boolean): void {
    if (value === this.hi_lo_unlocked) return;
    this.hi_lo_unlocked = value;
    this.hiLoUpdates.set({
      enabled: this.hi_lo_enabled,
      environment: this.hi_lo_environment,
      unlocked: this.hi_lo_unlocked,
    });
  }

  private sync_core_token(token: string | null): void {
    if (coreApi?.configure) {
      coreApi.configure(appConfig.apiUrl, token || undefined);
    } else if (coreApi?.setToken) {
      coreApi.setToken(token || undefined);
    } else {
      setToken(token || null);
    }
  }
}
